import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { RootState, AppDispatch } from '../store';
import {
  fetchPhases,
  fetchPhaseEntries,
  updatePhaseStatus,
  createPhaseEntry,
  editPhaseEntry,
  deletePhaseEntry,
  clearPhases,
  ProjectPhase,
  PhaseEntry,
} from '../store/projectPhasesSlice';
import {
  fetchPhaseTasks,
  createPhaseTask,
  editPhaseTask,
  deletePhaseTask,
  clearTasks,
  ProjectTask,
} from '../store/projectTasksSlice';
import api from '../services/api';

const STATUSES = ['Not Started', 'In Progress', 'Complete'] as const;
type Status = typeof STATUSES[number];
type PanelTab = 'updates' | 'tasks';

const COLUMN_STYLES: Record<Status, { header: string; bg: string; border: string; badge: string }> = {
  'Not Started': {
    header: 'bg-gray-100 text-gray-700 border-gray-200',
    bg: 'bg-gray-50',
    border: 'border-gray-200',
    badge: 'bg-gray-200 text-gray-600',
  },
  'In Progress': {
    header: 'bg-blue-100 text-blue-800 border-blue-200',
    bg: 'bg-blue-50',
    border: 'border-blue-200',
    badge: 'bg-blue-200 text-blue-700',
  },
  'Complete': {
    header: 'bg-green-100 text-green-800 border-green-200',
    bg: 'bg-green-50',
    border: 'border-green-200',
    badge: 'bg-green-200 text-green-700',
  },
};

interface Designer {
  _id: string;
  name: string;
  email: string;
}

interface BlobCache { [url: string]: string }

export default function ProjectManagementScreen() {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { user, token } = useSelector((state: RootState) => state.auth);
  const { phases, entries, phasesLoading, entriesLoading } = useSelector(
    (state: RootState) => state.projectPhases
  );
  const { tasks, loading: tasksLoading } = useSelector(
    (state: RootState) => state.projectTasks
  );

  const [projectName, setProjectName] = useState('');
  const [trackingEnabled, setTrackingEnabled] = useState(false);
  const [designers, setDesigners] = useState<Designer[]>([]);

  // Drag state
  const [draggingPhaseId, setDraggingPhaseId] = useState<string | null>(null);
  const [dragOverStatus, setDragOverStatus] = useState<Status | null>(null);

  // Detail panel (slide-over)
  const [openPhaseId, setOpenPhaseId] = useState<string | null>(null);
  const [panelTab, setPanelTab] = useState<PanelTab>('updates');

  // Update (entry) form state
  const [showAddUpdate, setShowAddUpdate] = useState(false);
  const [entryNotes, setEntryNotes] = useState('');
  const [entryFiles, setEntryFiles] = useState<File[]>([]);
  const [entryFilePreviews, setEntryFilePreviews] = useState<string[]>([]);
  const [postToTracking, setPostToTracking] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [editingEntry, setEditingEntry] = useState<PhaseEntry | null>(null);
  const [editNotes, setEditNotes] = useState('');

  // Task form state
  const [showAddTask, setShowAddTask] = useState(false);
  const [taskNotes, setTaskNotes] = useState('');
  const [taskFiles, setTaskFiles] = useState<File[]>([]);
  const [taskFilePreviews, setTaskFilePreviews] = useState<string[]>([]);
  const [taskAssignedTo, setTaskAssignedTo] = useState('');
  const [taskSubmitting, setTaskSubmitting] = useState(false);
  const [editingTask, setEditingTask] = useState<ProjectTask | null>(null);
  const [editTaskNotes, setEditTaskNotes] = useState('');
  const [editTaskAssignedTo, setEditTaskAssignedTo] = useState('');

  const blobCacheRef = useRef<BlobCache>({});
  const [blobCache, setBlobCache] = useState<BlobCache>({});
  const fileInputRef = useRef<HTMLInputElement>(null);
  const taskFileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!projectId) return;
    dispatch(fetchPhases(projectId));
    api.get(`/projects/${projectId}`).then((res) => {
      setProjectName(res.data.name || '');
      setTrackingEnabled(res.data.trackingEnabled || false);

      // Build the list of designers: owner + accepted collaborators
      const list: Designer[] = [];
      if (res.data.designer) {
        list.push({ _id: res.data.designer._id, name: res.data.designer.name, email: res.data.designer.email });
      }
      (res.data.collaborators || []).forEach((c: any) => {
        if (c.status === 'accepted' && c.user) {
          list.push({ _id: c.user._id, name: c.user.name, email: c.user.email });
        }
      });
      setDesigners(list);
      if (list.length > 0) setTaskAssignedTo(list[0]._id);
    }).catch(() => {});
    return () => {
      dispatch(clearPhases());
      dispatch(clearTasks());
      Object.values(blobCacheRef.current).forEach((u) => URL.revokeObjectURL(u));
    };
  }, [projectId, dispatch]);

  // --- Drag handlers ---
  const handleDragStart = (e: React.DragEvent, phaseId: string) => {
    e.dataTransfer.setData('phaseId', phaseId);
    e.dataTransfer.effectAllowed = 'move';
    setDraggingPhaseId(phaseId);
  };

  const handleDragEnd = () => {
    setDraggingPhaseId(null);
    setDragOverStatus(null);
  };

  const handleDragOver = (e: React.DragEvent, status: Status) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverStatus(status);
  };

  const handleDragLeave = () => {
    setDragOverStatus(null);
  };

  const handleDrop = async (e: React.DragEvent, targetStatus: Status) => {
    e.preventDefault();
    const phaseId = e.dataTransfer.getData('phaseId');
    setDraggingPhaseId(null);
    setDragOverStatus(null);
    const phase = phases.find((p) => p._id === phaseId);
    if (!phase || phase.status === targetStatus) return;
    await dispatch(updatePhaseStatus({ phaseId, status: targetStatus }));
  };

  // --- Detail panel ---
  const openPhaseDetail = (phaseId: string) => {
    setOpenPhaseId(phaseId);
    setPanelTab('updates');
    setShowAddUpdate(false);
    setShowAddTask(false);
    if (!entries[phaseId]) dispatch(fetchPhaseEntries(phaseId));
    if (!tasks[phaseId]) dispatch(fetchPhaseTasks(phaseId));
  };

  const closeDetail = () => {
    setOpenPhaseId(null);
    setShowAddUpdate(false);
    setShowAddTask(false);
    setEditingEntry(null);
    setEditingTask(null);
    entryFilePreviews.forEach((u) => URL.revokeObjectURL(u));
    taskFilePreviews.forEach((u) => URL.revokeObjectURL(u));
    setEntryFiles([]);
    setEntryFilePreviews([]);
    setTaskFiles([]);
    setTaskFilePreviews([]);
  };

  const switchTab = (tab: PanelTab) => {
    setPanelTab(tab);
    setShowAddUpdate(false);
    setShowAddTask(false);
    setEditingEntry(null);
    setEditingTask(null);
  };

  // --- Update (entry) file handlers ---
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    setEntryFiles((prev) => [...prev, ...files]);
    setEntryFilePreviews((prev) => [...prev, ...files.map((f) => URL.createObjectURL(f))]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeFile = (i: number) => {
    URL.revokeObjectURL(entryFilePreviews[i]);
    setEntryFiles((prev) => prev.filter((_, idx) => idx !== i));
    setEntryFilePreviews((prev) => prev.filter((_, idx) => idx !== i));
  };

  // --- Task file handlers ---
  const handleTaskFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    setTaskFiles((prev) => [...prev, ...files]);
    setTaskFilePreviews((prev) => [...prev, ...files.map((f) => URL.createObjectURL(f))]);
    if (taskFileInputRef.current) taskFileInputRef.current.value = '';
  };

  const removeTaskFile = (i: number) => {
    URL.revokeObjectURL(taskFilePreviews[i]);
    setTaskFiles((prev) => prev.filter((_, idx) => idx !== i));
    setTaskFilePreviews((prev) => prev.filter((_, idx) => idx !== i));
  };

  // --- Update submit ---
  const handleSubmitUpdate = async () => {
    if (!openPhaseId) return;
    setSubmitting(true);
    try {
      await dispatch(createPhaseEntry({ phaseId: openPhaseId, notes: entryNotes, files: entryFiles, postToTracking })).unwrap();
      setShowAddUpdate(false);
      setEntryNotes('');
      entryFilePreviews.forEach((u) => URL.revokeObjectURL(u));
      setEntryFiles([]);
      setEntryFilePreviews([]);
      setPostToTracking(false);
    } catch (err: any) {
      alert(`Failed to add update: ${err}`);
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditEntry = async () => {
    if (!editingEntry || !openPhaseId) return;
    setSubmitting(true);
    try {
      await dispatch(editPhaseEntry({ phaseId: openPhaseId, entryId: editingEntry._id, notes: editNotes })).unwrap();
      setEditingEntry(null);
    } catch (err: any) {
      alert(`Failed to update: ${err}`);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteEntry = async (entryId: string) => {
    if (!openPhaseId || !window.confirm('Delete this update? This cannot be undone.')) return;
    await dispatch(deletePhaseEntry({ phaseId: openPhaseId, entryId }));
  };

  // --- Task submit ---
  const handleSubmitTask = async () => {
    if (!openPhaseId) return;
    if (!taskAssignedTo) { alert('Please select a designer to assign this task to.'); return; }
    setTaskSubmitting(true);
    try {
      await dispatch(createPhaseTask({ phaseId: openPhaseId, notes: taskNotes, files: taskFiles, assignedTo: taskAssignedTo })).unwrap();
      setShowAddTask(false);
      setTaskNotes('');
      taskFilePreviews.forEach((u) => URL.revokeObjectURL(u));
      setTaskFiles([]);
      setTaskFilePreviews([]);
      if (designers.length > 0) setTaskAssignedTo(designers[0]._id);
    } catch (err: any) {
      alert(`Failed to add task: ${err}`);
    } finally {
      setTaskSubmitting(false);
    }
  };

  const handleEditTask = async () => {
    if (!editingTask || !openPhaseId) return;
    setTaskSubmitting(true);
    try {
      await dispatch(editPhaseTask({ phaseId: openPhaseId, taskId: editingTask._id, notes: editTaskNotes, assignedTo: editTaskAssignedTo || undefined })).unwrap();
      setEditingTask(null);
    } catch (err: any) {
      alert(`Failed to update task: ${err}`);
    } finally {
      setTaskSubmitting(false);
    }
  };

  const handleToggleTaskComplete = async (task: ProjectTask) => {
    if (!openPhaseId) return;
    await dispatch(editPhaseTask({ phaseId: openPhaseId, taskId: task._id, isComplete: !task.isComplete }));
  };

  const handleDeleteTask = async (taskId: string) => {
    if (!openPhaseId || !window.confirm('Delete this task? This cannot be undone.')) return;
    await dispatch(deletePhaseTask({ phaseId: openPhaseId, taskId }));
  };

  const getMediaBlobUrl = async (rawUrl: string): Promise<string> => {
    if (blobCacheRef.current[rawUrl]) return blobCacheRef.current[rawUrl];
    try {
      const resp = await fetch(rawUrl, { headers: { Authorization: `Bearer ${token}` } });
      const blob = await resp.blob();
      const blobUrl = URL.createObjectURL(blob);
      blobCacheRef.current[rawUrl] = blobUrl;
      setBlobCache((prev) => ({ ...prev, [rawUrl]: blobUrl }));
      return blobUrl;
    } catch {
      return rawUrl;
    }
  };

  const openPhase = openPhaseId ? phases.find((p) => p._id === openPhaseId) : null;
  const openEntries = openPhaseId ? (entries[openPhaseId] || []) : [];
  const openEntriesLoading = openPhaseId ? (entriesLoading[openPhaseId] || false) : false;
  const openTasks = openPhaseId ? (tasks[openPhaseId] || []) : [];
  const openTasksLoading = openPhaseId ? (tasksLoading[openPhaseId] || false) : false;

  return (
    <div className="min-h-screen bg-gray-100 flex flex-col">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-gray-900">Project Management</h1>
          {projectName && <p className="text-sm text-gray-500 mt-0.5">{projectName}</p>}
        </div>
        <button
          onClick={() => navigate(-1)}
          className="bg-gray-600 text-white px-4 py-2 rounded-md hover:bg-gray-700 text-sm transition-colors"
        >
          Back
        </button>
      </div>

      {/* Kanban board */}
      <div className="flex-1 overflow-x-auto p-6">
        {phasesLoading ? (
          <div className="text-center py-12 text-gray-500">Loading phases...</div>
        ) : (
          <div className="flex gap-5 min-h-[600px] min-w-[700px]">
            {STATUSES.map((status) => {
              const col = COLUMN_STYLES[status];
              const columnPhases = phases.filter((p) => p.status === status);
              const isDropTarget = dragOverStatus === status;

              return (
                <div
                  key={status}
                  className={`flex-1 flex flex-col rounded-xl border-2 transition-colors ${col.border} ${isDropTarget ? 'border-indigo-400 bg-indigo-50' : col.bg}`}
                  onDragOver={(e) => handleDragOver(e, status)}
                  onDragLeave={handleDragLeave}
                  onDrop={(e) => handleDrop(e, status)}
                >
                  {/* Column header */}
                  <div className={`px-4 py-3 rounded-t-xl border-b ${col.header} ${col.border} flex items-center justify-between`}>
                    <span className="font-semibold text-sm tracking-wide uppercase">{status}</span>
                    <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${col.badge}`}>
                      {columnPhases.length}
                    </span>
                  </div>

                  {/* Cards */}
                  <div className="flex-1 p-3 space-y-3 overflow-y-auto">
                    {columnPhases.map((phase) => (
                      <PhaseCard
                        key={phase._id}
                        phase={phase}
                        isDragging={draggingPhaseId === phase._id}
                        onDragStart={(e) => handleDragStart(e, phase._id)}
                        onDragEnd={handleDragEnd}
                        onOpenDetail={() => openPhaseDetail(phase._id)}
                      />
                    ))}

                    {columnPhases.length === 0 && isDropTarget && (
                      <div className="border-2 border-dashed border-indigo-300 rounded-lg h-20 flex items-center justify-center text-indigo-400 text-sm">
                        Drop here
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Phase detail slide-over */}
      {openPhase && (
        <div className="fixed inset-0 z-40 flex">
          {/* Backdrop */}
          <div className="flex-1 bg-black bg-opacity-30" onClick={closeDetail} />

          {/* Panel */}
          <div className="w-full max-w-md bg-white shadow-xl flex flex-col overflow-hidden">
            {/* Panel header */}
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between bg-indigo-700 text-white">
              <div>
                <h2 className="text-lg font-semibold">{openPhase.phaseName}</h2>
                <p className="text-indigo-200 text-sm">{openPhase.status}</p>
              </div>
              <button onClick={closeDetail} className="text-white hover:text-indigo-200 text-2xl leading-none">×</button>
            </div>

            {/* Tabs */}
            <div className="flex border-b border-gray-200 bg-white">
              <button
                onClick={() => switchTab('updates')}
                className={`flex-1 py-2.5 text-sm font-medium transition-colors ${
                  panelTab === 'updates'
                    ? 'border-b-2 border-indigo-600 text-indigo-700'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                Updates
              </button>
              <button
                onClick={() => switchTab('tasks')}
                className={`flex-1 py-2.5 text-sm font-medium transition-colors ${
                  panelTab === 'tasks'
                    ? 'border-b-2 border-indigo-600 text-indigo-700'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                Tasks
                {openTasks.filter((t) => !t.isComplete).length > 0 && (
                  <span className="ml-1.5 bg-indigo-100 text-indigo-700 text-xs font-bold px-1.5 py-0.5 rounded-full">
                    {openTasks.filter((t) => !t.isComplete).length}
                  </span>
                )}
              </button>
            </div>

            {/* Panel body */}
            <div className="flex-1 overflow-y-auto p-4">

              {/* ── UPDATES TAB ── */}
              {panelTab === 'updates' && (
                <>
                  {openEntriesLoading ? (
                    <div className="text-center py-8 text-gray-400">Loading...</div>
                  ) : openEntries.length === 0 && !showAddUpdate ? (
                    <div className="text-center py-8 text-gray-400 text-sm">No updates yet. Add a note or screenshot below.</div>
                  ) : (
                    <div className="space-y-4">
                      {openEntries.map((entry) =>
                        editingEntry?._id === entry._id ? (
                          <div key={entry._id} className="border border-indigo-300 rounded-lg p-3 bg-indigo-50">
                            <textarea
                              value={editNotes}
                              onChange={(e) => setEditNotes(e.target.value)}
                              rows={3}
                              className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-indigo-400 mb-2"
                            />
                            <div className="flex gap-2">
                              <button onClick={() => setEditingEntry(null)} className="flex-1 text-sm py-1 bg-gray-200 text-gray-700 rounded hover:bg-gray-300">Cancel</button>
                              <button onClick={handleEditEntry} disabled={submitting} className="flex-1 text-sm py-1 bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:opacity-50">Save</button>
                            </div>
                          </div>
                        ) : (
                          <EntryItem
                            key={entry._id}
                            entry={entry}
                            blobCache={blobCache}
                            getMediaBlobUrl={getMediaBlobUrl}
                            onEdit={() => { setEditingEntry(entry); setEditNotes(entry.notes); }}
                            onDelete={() => handleDeleteEntry(entry._id)}
                          />
                        )
                      )}
                    </div>
                  )}

                  {/* Add update form */}
                  {showAddUpdate && (
                    <div className="mt-4 border border-gray-200 rounded-lg p-4 bg-white">
                      <h3 className="text-sm font-semibold text-gray-700 mb-3">New Update</h3>
                      <textarea
                        value={entryNotes}
                        onChange={(e) => setEntryNotes(e.target.value)}
                        rows={3}
                        placeholder="Add notes..."
                        className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-indigo-400 mb-3"
                      />

                      <div className="mb-3">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-xs font-medium text-gray-600">Media</span>
                          <button type="button" onClick={() => fileInputRef.current?.click()} className="text-xs text-indigo-600 hover:text-indigo-800 font-medium">+ Add Files</button>
                        </div>
                        <input ref={fileInputRef} type="file" multiple accept="image/*,video/*" onChange={handleFileChange} className="hidden" />
                        {entryFilePreviews.length > 0 && (
                          <div className="flex flex-wrap gap-1.5">
                            {entryFilePreviews.map((p, i) => (
                              <div key={i} className="relative w-16 h-16">
                                <img src={p} alt="" className="w-full h-full object-cover rounded" />
                                <button onClick={() => removeFile(i)} className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-4 h-4 flex items-center justify-center text-xs leading-none">×</button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>

                      <div className={`flex items-center justify-between py-2 border-t border-gray-100 mb-3 ${!trackingEnabled ? 'opacity-60' : ''}`}>
                        <div>
                          <div className="text-xs font-medium text-gray-700">Post to Project Tracking</div>
                          {!trackingEnabled && <div className="text-xs text-gray-400 mt-0.5">Enable Project Tracking to post to the feed.</div>}
                        </div>
                        <input
                          type="checkbox"
                          checked={postToTracking}
                          onChange={(e) => setPostToTracking(e.target.checked)}
                          disabled={!trackingEnabled}
                          className="h-4 w-4 text-indigo-600 rounded disabled:cursor-not-allowed"
                        />
                      </div>

                      <div className="flex gap-2">
                        <button onClick={() => setShowAddUpdate(false)} disabled={submitting} className="flex-1 text-sm py-1.5 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 disabled:opacity-50">Cancel</button>
                        <button onClick={handleSubmitUpdate} disabled={submitting} className="flex-1 text-sm py-1.5 bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:opacity-50">{submitting ? 'Saving...' : 'Add Update'}</button>
                      </div>
                    </div>
                  )}
                </>
              )}

              {/* ── TASKS TAB ── */}
              {panelTab === 'tasks' && (
                <>
                  {openTasksLoading ? (
                    <div className="text-center py-8 text-gray-400">Loading...</div>
                  ) : openTasks.length === 0 && !showAddTask ? (
                    <div className="text-center py-8 text-gray-400 text-sm">No tasks yet. Add one below.</div>
                  ) : (
                    <div className="space-y-3">
                      {openTasks.map((task) =>
                        editingTask?._id === task._id ? (
                          <div key={task._id} className="border border-indigo-300 rounded-lg p-3 bg-indigo-50">
                            <textarea
                              value={editTaskNotes}
                              onChange={(e) => setEditTaskNotes(e.target.value)}
                              rows={3}
                              className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-indigo-400 mb-2"
                            />
                            {designers.length > 1 && (
                              <select
                                value={editTaskAssignedTo}
                                onChange={(e) => setEditTaskAssignedTo(e.target.value)}
                                className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-indigo-400 mb-2"
                              >
                                {designers.map((d) => (
                                  <option key={d._id} value={d._id}>{d.name}</option>
                                ))}
                              </select>
                            )}
                            <div className="flex gap-2">
                              <button onClick={() => setEditingTask(null)} className="flex-1 text-sm py-1 bg-gray-200 text-gray-700 rounded hover:bg-gray-300">Cancel</button>
                              <button onClick={handleEditTask} disabled={taskSubmitting} className="flex-1 text-sm py-1 bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:opacity-50">Save</button>
                            </div>
                          </div>
                        ) : (
                          <TaskItem
                            key={task._id}
                            task={task}
                            blobCache={blobCache}
                            getMediaBlobUrl={getMediaBlobUrl}
                            onToggleComplete={() => handleToggleTaskComplete(task)}
                            onEdit={() => {
                              setEditingTask(task);
                              setEditTaskNotes(task.notes);
                              setEditTaskAssignedTo(task.assignedTo?._id || '');
                            }}
                            onDelete={() => handleDeleteTask(task._id)}
                          />
                        )
                      )}
                    </div>
                  )}

                  {/* Add task form */}
                  {showAddTask && (
                    <div className="mt-4 border border-gray-200 rounded-lg p-4 bg-white">
                      <h3 className="text-sm font-semibold text-gray-700 mb-3">New Task</h3>
                      <textarea
                        value={taskNotes}
                        onChange={(e) => setTaskNotes(e.target.value)}
                        rows={3}
                        placeholder="Describe the task..."
                        className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-indigo-400 mb-3"
                      />

                      <div className="mb-3">
                        <label className="block text-xs font-medium text-gray-600 mb-1">Assign to</label>
                        {designers.length === 0 ? (
                          <p className="text-xs text-gray-400">No designers found for this project.</p>
                        ) : (
                          <select
                            value={taskAssignedTo}
                            onChange={(e) => setTaskAssignedTo(e.target.value)}
                            className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-indigo-400"
                          >
                            {designers.map((d) => (
                              <option key={d._id} value={d._id}>{d.name}</option>
                            ))}
                          </select>
                        )}
                      </div>

                      <div className="mb-3">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-xs font-medium text-gray-600">Images (Optional)</span>
                          <button type="button" onClick={() => taskFileInputRef.current?.click()} className="text-xs text-indigo-600 hover:text-indigo-800 font-medium">+ Add Files</button>
                        </div>
                        <input ref={taskFileInputRef} type="file" multiple accept="image/*,video/*" onChange={handleTaskFileChange} className="hidden" />
                        {taskFilePreviews.length > 0 && (
                          <div className="flex flex-wrap gap-1.5">
                            {taskFilePreviews.map((p, i) => (
                              <div key={i} className="relative w-16 h-16">
                                <img src={p} alt="" className="w-full h-full object-cover rounded" />
                                <button onClick={() => removeTaskFile(i)} className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-4 h-4 flex items-center justify-center text-xs leading-none">×</button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>

                      <p className="text-xs text-gray-400 mb-3">The assigned designer will be notified automatically.</p>

                      <div className="flex gap-2">
                        <button onClick={() => setShowAddTask(false)} disabled={taskSubmitting} className="flex-1 text-sm py-1.5 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 disabled:opacity-50">Cancel</button>
                        <button onClick={handleSubmitTask} disabled={taskSubmitting || !taskAssignedTo} className="flex-1 text-sm py-1.5 bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:opacity-50">{taskSubmitting ? 'Saving...' : 'Add Task'}</button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Panel footer */}
            {panelTab === 'updates' && !showAddUpdate && (
              <div className="px-4 py-3 border-t border-gray-200">
                <button
                  onClick={() => { setShowAddUpdate(true); setEditingEntry(null); setEntryNotes(''); setEntryFiles([]); setEntryFilePreviews([]); setPostToTracking(false); }}
                  className="w-full bg-indigo-600 text-white py-2 rounded-lg text-sm font-medium hover:bg-indigo-700 transition-colors"
                >
                  + Add Update
                </button>
              </div>
            )}
            {panelTab === 'tasks' && !showAddTask && (
              <div className="px-4 py-3 border-t border-gray-200">
                <button
                  onClick={() => { setShowAddTask(true); setEditingTask(null); setTaskNotes(''); setTaskFiles([]); setTaskFilePreviews([]); if (designers.length > 0) setTaskAssignedTo(designers[0]._id); }}
                  className="w-full bg-indigo-600 text-white py-2 rounded-lg text-sm font-medium hover:bg-indigo-700 transition-colors"
                >
                  + Add Task
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Phase card (draggable) ─────────────────────────────────────────────────

interface PhaseCardProps {
  phase: ProjectPhase;
  isDragging: boolean;
  onDragStart: (e: React.DragEvent) => void;
  onDragEnd: () => void;
  onOpenDetail: () => void;
}

function PhaseCard({ phase, isDragging, onDragStart, onDragEnd, onOpenDetail }: PhaseCardProps) {
  return (
    <div
      draggable
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onClick={onOpenDetail}
      className={`bg-white rounded-lg border border-gray-200 p-3 cursor-grab active:cursor-grabbing shadow-sm hover:shadow-md transition-all select-none ${isDragging ? 'opacity-40 rotate-1 scale-95' : ''}`}
    >
      <div className="flex items-start justify-between gap-2">
        <span className="font-medium text-gray-800 text-sm leading-snug">{phase.phaseName}</span>
        <svg className="w-4 h-4 text-gray-300 shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
          <path d="M7 2a2 2 0 1 0 .001 4.001A2 2 0 0 0 7 2zm0 6a2 2 0 1 0 .001 4.001A2 2 0 0 0 7 8zm0 6a2 2 0 1 0 .001 4.001A2 2 0 0 0 7 14zm6-8a2 2 0 1 0-.001-4.001A2 2 0 0 0 13 6zm0 2a2 2 0 1 0 .001 4.001A2 2 0 0 0 13 8zm0 6a2 2 0 1 0 .001 4.001A2 2 0 0 0 13 14z" />
        </svg>
      </div>
      {phase.entryCount > 0 && (
        <div className="mt-2 text-xs text-gray-400">{phase.entryCount} {phase.entryCount === 1 ? 'update' : 'updates'}</div>
      )}
    </div>
  );
}

// ─── Entry item ─────────────────────────────────────────────────────────────

function EntryItem({
  entry,
  blobCache,
  getMediaBlobUrl,
  onEdit,
  onDelete,
}: {
  entry: PhaseEntry;
  blobCache: BlobCache;
  getMediaBlobUrl: (url: string) => Promise<string>;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const [localBlobUrls, setLocalBlobUrls] = useState<{ [url: string]: string }>({});
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    entry.mediaUrls?.forEach((url) => {
      if (!localBlobUrls[url]) {
        getMediaBlobUrl(url).then((b) => setLocalBlobUrls((prev) => ({ ...prev, [url]: b })));
      }
    });
  }, [entry.mediaUrls]);

  return (
    <div className="border border-gray-100 rounded-lg p-3 bg-gray-50">
      <div className="flex items-start justify-between gap-2 mb-1">
        <div className="min-w-0">
          <span className="text-xs font-medium text-gray-600">{entry.addedBy?.name}</span>
          {entry.postedToTracking && <span className="ml-1.5 text-xs text-indigo-500">· Tracking</span>}
          <span className="ml-1.5 text-xs text-gray-400">· {new Date(entry.createdAt).toLocaleDateString()}</span>
        </div>
        <div className="flex gap-2 shrink-0">
          <button onClick={onEdit} className="text-xs text-blue-600 hover:text-blue-800">Edit</button>
          <button onClick={onDelete} className="text-xs text-red-500 hover:text-red-700">Delete</button>
        </div>
      </div>
      {entry.notes && (
        <>
          <p className={`text-sm text-gray-700 ${!expanded && entry.notes.length > 100 ? 'line-clamp-2' : ''}`}>{entry.notes}</p>
          {entry.notes.length > 100 && (
            <button onClick={() => setExpanded(!expanded)} className="text-xs text-indigo-500 mt-0.5">{expanded ? 'Show less' : 'Show more'}</button>
          )}
        </>
      )}
      {entry.mediaUrls && entry.mediaUrls.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-2">
          {entry.mediaUrls.map((url, i) => {
            const blob = localBlobUrls[url] || url;
            const isVideo = entry.media[i]?.contentType?.startsWith('video/');
            return isVideo ? (
              <div key={i} className="w-14 h-14 bg-gray-200 rounded flex items-center justify-center text-gray-400 text-xs">▶</div>
            ) : (
              <img key={i} src={blob} alt="" onClick={() => window.open(blob, '_blank')} className="w-14 h-14 object-cover rounded cursor-pointer hover:opacity-80" />
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Task item ───────────────────────────────────────────────────────────────

function TaskItem({
  task,
  blobCache,
  getMediaBlobUrl,
  onToggleComplete,
  onEdit,
  onDelete,
}: {
  task: ProjectTask;
  blobCache: BlobCache;
  getMediaBlobUrl: (url: string) => Promise<string>;
  onToggleComplete: () => void;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const [localBlobUrls, setLocalBlobUrls] = useState<{ [url: string]: string }>({});
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    task.mediaUrls?.forEach((url) => {
      if (!localBlobUrls[url]) {
        getMediaBlobUrl(url).then((b) => setLocalBlobUrls((prev) => ({ ...prev, [url]: b })));
      }
    });
  }, [task.mediaUrls]);

  return (
    <div className={`border rounded-lg p-3 ${task.isComplete ? 'bg-green-50 border-green-100' : 'bg-white border-gray-200'}`}>
      <div className="flex items-start gap-2 mb-1">
        {/* Completion checkbox */}
        <button
          onClick={onToggleComplete}
          className={`mt-0.5 shrink-0 w-4 h-4 rounded border flex items-center justify-center transition-colors ${
            task.isComplete
              ? 'bg-green-500 border-green-500 text-white'
              : 'border-gray-300 hover:border-indigo-400'
          }`}
        >
          {task.isComplete && (
            <svg className="w-3 h-3" fill="none" viewBox="0 0 12 12" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M2 6l3 3 5-5" />
            </svg>
          )}
        </button>

        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <div>
              <span className="text-xs font-medium text-gray-600">
                Assigned to {task.assignedTo?.name || '—'}
              </span>
              <span className="ml-1.5 text-xs text-gray-400">· {new Date(task.createdAt).toLocaleDateString()}</span>
              {task.isComplete && <span className="ml-1.5 text-xs text-green-600 font-medium">· Complete</span>}
            </div>
            <div className="flex gap-2 shrink-0">
              <button onClick={onEdit} className="text-xs text-blue-600 hover:text-blue-800">Edit</button>
              <button onClick={onDelete} className="text-xs text-red-500 hover:text-red-700">Delete</button>
            </div>
          </div>

          {task.notes && (
            <>
              <p className={`text-sm mt-1 ${task.isComplete ? 'text-gray-400 line-through' : 'text-gray-700'} ${!expanded && task.notes.length > 100 ? 'line-clamp-2' : ''}`}>
                {task.notes}
              </p>
              {task.notes.length > 100 && (
                <button onClick={() => setExpanded(!expanded)} className="text-xs text-indigo-500 mt-0.5">{expanded ? 'Show less' : 'Show more'}</button>
              )}
            </>
          )}

          {task.mediaUrls && task.mediaUrls.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-2">
              {task.mediaUrls.map((url, i) => {
                const blob = localBlobUrls[url] || url;
                const isVideo = task.media[i]?.contentType?.startsWith('video/');
                return isVideo ? (
                  <div key={i} className="w-14 h-14 bg-gray-200 rounded flex items-center justify-center text-gray-400 text-xs">▶</div>
                ) : (
                  <img key={i} src={blob} alt="" onClick={() => window.open(blob, '_blank')} className="w-14 h-14 object-cover rounded cursor-pointer hover:opacity-80" />
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
